import { useMutation, useQueryClient } from '@tanstack/react-query'


import { updateNetworkingDeclaration } from './api'
import { networkingDeclarationKey } from './queries'

import type { NetworkingFormValues } from './types'
import type { UseFormSetError } from 'react-hook-form'

const toPayload = (values: NetworkingFormValues) => ({
  record: {
    networking_opt_in: values.networking_opt_in,
    networking_tag_ids: values.networking_tag_ids,
    networking_profile: {
      headline: values.headline,
      company: values.company,
      job_title: values.job_title
    }
  }
})

/**
 * Consentimento, tags e apresentação vão em uma única escrita — a api envolve as
 * duas gravações na mesma transação, então um 422 nunca deixa metade aplicada.
 */
export const useUpdateNetworkingDeclaration = (
  digitalProductId: string | number | null | undefined,
  setError?: UseFormSetError<NetworkingFormValues>
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: NetworkingFormValues) =>
      updateNetworkingDeclaration(digitalProductId as string | number, toPayload(values)),

    onSuccess: declaration => {
      queryClient.setQueryData(
        networkingDeclarationKey(digitalProductId ?? ''),
        declaration
      )
    },

    onError: (error: unknown) => {
      // A BaseController da api responde `{ record, errors, error_message }`, com
      // `errors` sendo um hash `atributo → [mensagens]`. Não há `code` nem
      // `pointer` neste backend, e o app não tem i18n — as mensagens vêm em
      // PT-BR do próprio servidor.
      const errors = (
        error as {
          response?: { data?: { errors?: Record<string, string[] | string> } }
        }
      )?.response?.data?.errors

      if (!errors || !setError) return

      Object.entries(errors).forEach(([attribute, messages]) => {
        const message = Array.isArray(messages) ? messages[0] : String(messages)

        setError(attribute as keyof NetworkingFormValues, {
          type: 'server',
          message
        })
      })
    }
  })
}
